import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, statusColor, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Modal from "@/components/Modal";
import { Plus, Trophy, CalendarBlank, MapPin, Broadcast, Pencil, Trash, ArrowRight, Users, CurrencyInr } from "@phosphor-icons/react";
import BidTiersEditor from "@/components/BidTiersEditor";
import ImageInput from "@/components/ImageInput";
import { toast } from "sonner";

const TIERS = [
  { teams: 2, price: 0, label: "Starter", color: "#DC2626" },
  { teams: 4, price: 3000, label: "Rookie", color: "#0EA5E9" },
  { teams: 8, price: 4000, label: "Club", color: "#F59E0B" },
  { teams: 12, price: 5000, label: "League", color: "#2563EB" },
  { teams: 16, price: 6000, label: "State", color: "#0F766E" },
  { teams: 22, price: 7000, label: "National", color: "#7C3AED" },
  { teams: 30, price: 8000, label: "Championship", color: "#EA580C" },
];

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function TierSelector({ value, onChange }) {
  const activeTier = TIERS.reduce((best, t) => (t.teams >= value && (!best || t.teams < best.teams) ? t : best), null) || TIERS[TIERS.length - 1];
  return (
    <div>
      <label className="label-mono block mb-2">Teams In Auction — pick a plan</label>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="tier-selector">
        {TIERS.map((t) => {
          const selected = value === t.teams;
          return (
            <button
              type="button"
              key={t.teams}
              onClick={() => onChange(t.teams)}
              data-testid={`tier-${t.teams}`}
              className={`p-3 rounded border-2 transition-all text-left ${
                selected ? "shadow-md" : "border-gray-200 hover:border-gray-300"
              }`}
              style={selected ? { borderColor: t.color, background: `${t.color}0f` } : {}}
            >
              <div className="font-black text-3xl leading-none" style={{ color: t.color }}>{String(t.teams).padStart(2, "0")}</div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mt-1">Teams · {t.label}</div>
              <div className="text-sm font-bold mt-1 text-slate-900">{t.price === 0 ? "Free" : `₹${t.price.toLocaleString("en-IN")}`}</div>
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex flex-col sm:flex-row sm:items-end gap-3 bg-white border border-gray-200 rounded p-3">
        <div className="flex-1">
          <label className="label-mono block mb-1">Or enter exact team count (2–30)</label>
          <input
            type="number"
            min={2}
            max={30}
            step={1}
            value={value}
            onChange={(e) => {
              const n = Math.max(2, Math.min(30, parseInt(e.target.value, 10) || 2));
              onChange(n);
            }}
            data-testid="custom-team-count"
            className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none"
          />
        </div>
        <div className="text-xs text-gray-600 sm:pb-2 sm:max-w-[280px]">
          {value === activeTier.teams ? (
            <>Billed at <span className="font-bold text-slate-900">{activeTier.teams}-team {activeTier.label}</span> plan — {activeTier.price === 0 ? "Free" : `₹${activeTier.price.toLocaleString("en-IN")}`}.</>
          ) : (
            <>Nearest tier: <span className="font-bold text-slate-900">{activeTier.teams} teams · {activeTier.label}</span> — {activeTier.price === 0 ? "Free" : `₹${activeTier.price.toLocaleString("en-IN")}`}. You&apos;ll be billed at this tier.</>
          )}
        </div>
      </div>
    </div>
  );
}

function AuctionForm({ initial, onSave, formId, isEdit, onCancel, paying }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    number_of_teams: initial?.number_of_teams || 2,
    venue: initial?.venue || "",
    banner_url: initial?.banner_url || "",
    start_date: initial?.start_date?.slice(0, 10) || "",
    end_date: initial?.end_date?.slice(0, 10) || "",
    default_team_budget: initial?.settings?.default_team_budget ?? 1000,
    default_base_price: initial?.settings?.default_base_price ?? 10,
    bid_increments: initial?.settings?.bid_increments ?? [{ up_to: null, step: 10 }],
    max_players_per_team: initial?.settings?.max_players_per_team ?? 15,
    min_players_per_team: initial?.settings?.min_players_per_team ?? 11,
    timer_seconds: initial?.settings?.timer_seconds ?? 30,
  });

  const selectedTier = TIERS.reduce((best, t) => (t.teams >= form.number_of_teams && (!best || t.teams < best.teams) ? t : best), null) || TIERS[TIERS.length - 1];

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      number_of_teams: form.number_of_teams,
      venue: form.venue || null,
      banner_url: form.banner_url || null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      settings: {
        default_team_budget: Number(form.default_team_budget) || 1000,
        default_base_price: Number(form.default_base_price) || 10,
        bid_increments: form.bid_increments,
        max_players_per_team: Number(form.max_players_per_team) || 15,
        min_players_per_team: Number(form.min_players_per_team) || 11,
        timer_seconds: Number(form.timer_seconds) || 30,
      },
    };
    await onSave(payload, selectedTier);
  };

  return (
    <form id={formId} onSubmit={submit} data-testid="auction-form" className="space-y-6">
      {!isEdit && <TierSelector value={form.number_of_teams} onChange={(v) => setForm({ ...form, number_of_teams: v })} />}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="label-mono block mb-2">Auction / Tournament Name*</label>
          <input required data-testid="auction-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
        </div>
        <div className="sm:col-span-2">
          <ImageInput
            label="Tournament Banner (16:9 recommended)"
            value={form.banner_url}
            onChange={(v) => setForm({ ...form, banner_url: v })}
            testId="auction-banner-input"
            maxKB={5120}
            aspect="aspect-video"
          />
        </div>
        <div>
          <label className="label-mono block mb-2">Venue</label>
          <input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label-mono block mb-2">Start</label>
            <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="auction-start-date" className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
          </div>
          <div>
            <label className="label-mono block mb-2">End</label>
            <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
          </div>
        </div>
      </div>

      <details className="border border-gray-200 rounded" open>
        <summary className="px-4 py-2.5 cursor-pointer label-mono select-none">Advanced settings — budgets, timer, bid tiers</summary>
        <div className="p-4 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="label-mono block mb-2">Team Budget (pts)</label>
              <input type="number" min="10" value={form.default_team_budget} onChange={(e) => setForm({ ...form, default_team_budget: e.target.value })} data-testid="setting-team-budget" className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
            </div>
            <div>
              <label className="label-mono block mb-2">Base Price (pts)</label>
              <input type="number" min="1" value={form.default_base_price} onChange={(e) => setForm({ ...form, default_base_price: e.target.value })} data-testid="setting-base-price" className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
            </div>
            <div>
              <label className="label-mono block mb-2">Bid Timer (sec)</label>
              <input type="number" min="5" value={form.timer_seconds} onChange={(e) => setForm({ ...form, timer_seconds: e.target.value })} data-testid="setting-timer" className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
            </div>
            <div>
              <label className="label-mono block mb-2">Min Players</label>
              <input type="number" min="1" value={form.min_players_per_team} onChange={(e) => setForm({ ...form, min_players_per_team: e.target.value })} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
            </div>
            <div>
              <label className="label-mono block mb-2">Max Players</label>
              <input type="number" min="1" value={form.max_players_per_team} onChange={(e) => setForm({ ...form, max_players_per_team: e.target.value })} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
            </div>
          </div>
          <div className="mt-4">
            <BidTiersEditor value={form.bid_increments} onChange={(v) => setForm({ ...form, bid_increments: v })} />
          </div>
        </div>
      </details>

      {!isEdit && (
        <div className="border border-blue-200 bg-blue-50 rounded p-3 flex items-center justify-between gap-3" data-testid="tier-summary">
          <div className="flex items-center gap-2 text-sm">
            <Users size={16} className="text-blue-600" />
            <span>You&apos;ve chosen <span className="font-bold">{selectedTier.teams} teams · {selectedTier.label}</span></span>
          </div>
          <div className="font-bold text-blue-700 flex items-center gap-1">
            {selectedTier.price === 0 ? "Free" : <><CurrencyInr size={14} /> {selectedTier.price.toLocaleString("en-IN")}</>}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
        <button type="button" onClick={onCancel} disabled={paying} data-testid="cancel-auction" className="btn-ghost !px-5 !py-2 text-xs">Cancel</button>
        <button type="submit" disabled={paying} data-testid="submit-auction" className="btn-primary !px-5 !py-2 text-xs">
          {paying ? "Processing…" : (isEdit ? "Save" : "Create")}
        </button>
      </div>
    </form>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [paying, setPaying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auctions");
      setAuctions(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async (payload, tier) => {
    if (!tier || tier.price === 0) {
      // Free tier — direct create
      try {
        await api.post("/auctions", payload);
        toast.success("Auction created");
        setShowCreate(false);
        load();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); throw e; }
      return;
    }
    // Paid tier — Razorpay flow
    setPaying(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error("Razorpay checkout failed to load"); setPaying(false); return; }
      const { data: order } = await api.post("/payments/order", { number_of_teams: payload.number_of_teams });
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "NitiSoft Auction",
        description: `${tier.teams} teams · ${tier.label} plan`,
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#2563EB" },
        handler: async (res) => {
          try {
            await api.post("/auctions", {
              ...payload,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Payment confirmed — auction created");
            setShowCreate(false);
            load();
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Payment verified but auction creation failed");
          } finally { setPaying(false); }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on("payment.failed", () => { toast.error("Payment failed"); setPaying(false); });
      rzp.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to start payment");
      setPaying(false);
    }
  };

  const update = async (payload) => {
    try {
      await api.patch(`/auctions/${editing.id}`, payload);
      toast.success("Auction updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); throw e; }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete auction "${a.name}"? This removes all teams, players & audit logs.`)) return;
    try { await api.delete(`/auctions/${a.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="page-dark -mx-4 sm:-mx-6 px-4 sm:px-6 py-6 -my-6 sm:-my-8">
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="label-mono mb-2">MY WORKSPACE</div>
          <h1 className="font-display uppercase text-3xl sm:text-4xl">Hello, {user?.name?.split(" ")[0]}</h1>
          <p className="text-gray-500 mt-1 text-sm">Manage your tournaments and live auctions.</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="create-auction-button" className="btn-primary !px-4 !py-2 text-xs flex items-center gap-2">
          <Plus size={14} /> Create Auction
        </button>
      </div>

      {loading ? (
        <div className="label-mono py-24 text-center">Loading…</div>
      ) : auctions.length === 0 ? (
        <div className="border border-dashed border-gray-200 rounded p-16 text-center bg-white" data-testid="dashboard-empty">
          <Trophy weight="duotone" size={44} className="text-gray-400 mx-auto mb-3" />
          <div className="font-display text-3xl uppercase mb-2">No auctions yet</div>
          <p className="text-gray-500 mb-6">Create your first tournament auction to get started.</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary inline-flex items-center gap-2">
            <Plus size={14} /> Create Auction
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="auction-grid">
          {auctions.map((a) => {
            const start = a.start_date ? new Date(a.start_date) : null;
            return (
              <div key={a.id} className="bg-white border border-gray-200 rounded-md p-5 flex flex-col shadow-sm" data-testid={`dashboard-auction-${a.id}`}>
                <div className="flex items-start justify-between gap-2 mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-2xl uppercase leading-tight truncate">{a.name}</div>
                    {a.number_of_teams && <div className="text-xs text-gray-500 mt-1">{a.number_of_teams}-team plan</div>}
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded border whitespace-nowrap ${statusColor(a.status)}`}>
                    {a.status === "live" && <span className="live-dot mr-1 inline-block align-middle" />}
                    {a.status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-4">
                  {start && <span className="inline-flex items-center gap-1"><CalendarBlank size={14} /> {start.toLocaleDateString()}</span>}
                  {a.venue && <span className="inline-flex items-center gap-1"><MapPin size={14} /> {a.venue}</span>}
                </div>

                <div className="grid grid-cols-3 gap-3 text-center border-t border-b border-gray-200 py-3 mb-4">
                  <div><div className="font-display text-2xl">{a.team_count || 0}</div><div className="label-mono">Teams</div></div>
                  <div><div className="font-display text-2xl">{a.player_count || 0}</div><div className="label-mono">Players</div></div>
                  <div><div className="font-display text-2xl text-emerald-600">{a.sold_count || 0}</div><div className="label-mono">Sold</div></div>
                </div>

                <div className="flex items-center gap-2 mt-auto">
                  <Link to={`/auction/${a.id}/manage`} className="btn-primary text-xs flex-1 flex items-center justify-center gap-2" data-testid={`manage-auction-${a.id}`}>
                    Manage <ArrowRight size={12} />
                  </Link>
                  {a.status === "live" ? (
                    <Link to={`/auction/${a.id}/live`} className="btn-ghost text-xs flex items-center gap-1" title="Live Auction">
                      <Broadcast size={14} />
                    </Link>
                  ) : (
                    <span className="btn-ghost text-xs flex items-center gap-1 opacity-40 cursor-not-allowed" title={`Live only when status = live (currently ${a.status})`}>
                      <Broadcast size={14} />
                    </span>
                  )}
                  <button onClick={() => setEditing(a)} className="p-2 rounded hover:bg-gray-100" title="Edit" data-testid={`edit-auction-${a.id}`}>
                    <Pencil size={14} className="text-gray-500" />
                  </button>
                  <button onClick={() => del(a)} className="p-2 rounded hover:bg-red-50" title="Delete">
                    <Trash size={14} className="text-red-600" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={showCreate}
        onClose={() => !paying && setShowCreate(false)}
        title="Create Auction"
        subtitle="NEW TOURNAMENT"
        size="lg"
        testId="create-auction-modal"
      >
        <AuctionForm onSave={create} formId="create-auction-form-el" onCancel={() => setShowCreate(false)} paying={paying} />
      </Modal>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title="Edit Auction"
        subtitle="UPDATE"
        size="lg"
        testId="edit-auction-modal"
      >
        {editing && <AuctionForm initial={editing} onSave={update} formId="edit-auction-form-el" isEdit onCancel={() => setEditing(null)} />}
      </Modal>
    </div>
    </div>
  );
}
